import { Textarea, Textcomplete } from 'textcomplete';
import Contenteditable from 'textcomplete.contenteditable-ifixit';
import _ from 'lodash';
import EditorCache from 'Shared/prosemirror/editorcache.js';

// Function to add textcomplete to the relevant textboxes, this is used after
// we're sure that textcomplete and all its dependencies are loaded
const Mentions = new Class({
   initialize: function() {
      const itemTemplate = user => `
         <div class="name-complete-entry">
            <img src="${user.image}" class="name-complete-image">
            <span class="name-complete-unique-username">
               ${user.unique_username}
            </span>
            <span class="name-complete-username">
               ${user.username}
            </span>
         </div>`;
      const replaceUniqueUsername = user => `$1@${user.unique_username} `;
      function searchUsers(term, callback) {
         const userlist = Object.values(App.users);
         const MAX_USERS_SUGGESTED = 5;
         // If no search term, show the list of users participating in the
         // disussions on the page.
         if (!term && userlist) {
            // Grab the user list from the page
            callback(userlist.slice(0, MAX_USERS_SUGGESTED));
            return;
         }

         /**
          * Rank users for sorting displayed suggestions.
          * User ranking map (1 is highest rank):
          * 1. unique_username exact match
          * 2. username exact match
          * 3. unique_username start of string match
          * 4. username start of string match
          * 5. username or unique_username substring match
          */
         let matchedLocalUsers = [];
         userlist.forEach(u => {
            let rankedUser = _.clone(u);
            if (u.unique_username === term) {
               rankedUser.rank = 1;
            } else if (u.username === term) {
               rankedUser.rank = 2;
            } else if (u.unique_username.startsWith(term)) {
               rankedUser.rank = 3;
            } else if (u.username.startsWith(term)) {
               rankedUser.rank = 4;
            } else if (u.unique_username.includes(term) || u.username.includes(term)) {
               rankedUser.rank = 5;
            } else {
               return;
            }
            matchedLocalUsers.push(rankedUser);
         });

         // Sort matched users based on how well they matches term (i.e. rank)
         let compareUsersByRank = (u1, u2) => u1.rank - u2.rank;
         matchedLocalUsers.sort(compareUsersByRank);
         matchedLocalUsers.forEach(u => delete u.rank);

         // If there are more than 5 local users that match the term, show that
         // list and don't make a time-consuming request.
         if (matchedLocalUsers.length >= MAX_USERS_SUGGESTED) {
            callback(matchedLocalUsers.slice(0, MAX_USERS_SUGGESTED));
            return;
         }

         // If there is a search term this api request will fire. Note this
         // request only returns username, unique_username, and mini version
         // of their avatar image
         const request = new Request.API_2_0('users/search/' + term + '?limit=5&uniqueOnly', {
            method: 'GET',
            onSuccess: function(response) {
               var items = response.results
                  .filter(function(result) {
                     return result.username !== null && result.unique_username !== null;
                  })
                  .map(function(result) {
                     result.image = result.image['mini'];
                     return result;
                  });

               const noDuplicateUsers = _.uniqBy(
                  matchedLocalUsers.concat(items),
                  'unique_username'
               );
               callback(noDuplicateUsers.slice(0, MAX_USERS_SUGGESTED));
            },
         }).send();
      }
      $$('.js-user-mention').forEach(function(textarea, index) {
         // Assign a unique key to each textarea to map to its corresponding
         // dropdown menu.
         const KEY = `textarea${index}`;
         textarea.className += ' ' + KEY;
         const textcompleteOptions = {
            dropdown: {
               className: `dropdown-menu textcomplete-dropdown ${KEY} `,
            },
         };

         if (textarea.id) {
            textcompleteOptions.dropdown.className += textarea.id + '-dropdown';
         }
         const editor =
            textarea.tagName === 'TEXTAREA'
               ? new Textarea(textarea)
               : new Contenteditable(textarea);
         const textcomplete = new Textcomplete(editor, textcompleteOptions);
         // Disable enter button when dropdown opens so we can use it to
         // select the highlighted element.
         const onShow = () => {
            const id = textarea.dataset['editorId'];
            const cachedEditor = EditorCache.get(id);
            cachedEditor && cachedEditor.disableEnter();
         };
         // Re-enable enter button when dropdown closes.
         const onHide = () => {
            const id = textarea.dataset['editorId'];
            const cachedEditor = EditorCache.get(id);
            cachedEditor && cachedEditor.enableEnter();
         };
         // Add event listeners to close the dropdown menu when the textarea is
         // blurred, and display it again when focus is regained.
         const onShown = () => {
            const dropdown = document.querySelector(`.dropdown-menu.textcomplete-dropdown.${KEY}`);
            function hideDropdown() {
               dropdown.style.display = 'none';
            }
            function showDropdown() {
               dropdown.style.display = 'initial';
            }
            textarea.addEvent('blur', hideDropdown);
            textarea.addEvent('focus', showDropdown);
         };
         // Highlight first result automatically.
         const onRendered = () => {
            if (textcomplete.dropdown.items.length) {
               // Activate the first item by default.
               textcomplete.dropdown.items[0].activate();
            }
         };
         const searchNamesStrategy = {
            // Match up to two words after '@' including an optional space in between
            match: /(^|\s)@((\w*)(\s\w+)?)$/,
            search: _.debounce(searchUsers, 400),
            template: itemTemplate,
            replace: replaceUniqueUsername,
            cache: false,
         };
         textcomplete
            .register([searchNamesStrategy], { maxCount: 5 })
            .on('show', onShow)
            .on('hide', onHide)
            .on('shown', onShown)
            .on('rendered', onRendered);
      });
   },
});

let textCompleteLoaded = false;

const initTextComplete = function(userMention) {
   function addScripts() {
      if (!textCompleteLoaded) {
         new Mentions();
         textCompleteLoaded = true;
      }

      // Remove the onfocus event since we only want to load the scripts once
      this.removeEvent('focus', addScripts);
   }

   // Add the focus event to all textboxes in which a user can use mentions.
   // Needs to use $$ on the userMention, in case it's not from the `when` line
   // below.
   $$(userMention).addEvent('focus', addScripts);
};

when($$('.js-user-mention'), initTextComplete);

export default initTextComplete;
